import React, { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Bot,
  Send,
  UploadCloud,
  Mic,
  ArrowRight,
  ArrowDown
} from "lucide-react";
import { fetchAiSearchParts } from "@/store/aiAssistant/aiAssistantAction";
import { addMessage, openChat } from "../../store/aiAssistant/aiAssitantSlice";

const AiAssistantSection = ({ variants }) => {
  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState("");

  const handleInputChange = useCallback(
    (e) => setInputValue(e.target.value),
    []
  );

  const handleAskAssistant = useCallback(async () => {
    if (!inputValue.trim()) return;
    const userMessage = inputValue.trim();
    dispatch(openChat());
    dispatch(
      addMessage({
        sender: "user",
        type: "text",
        text: userMessage
      })
    );
    setInputValue("");
    try {
      const result = await dispatch(fetchAiSearchParts(userMessage)).unwrap();
      if (result.success) {
        if (result.results && result.results.length > 0 && result.aiResponse) {
          dispatch(
            addMessage({
              sender: "assistant",
              type: "text",
              text: result.aiResponse
            })
          );

          dispatch(
            addMessage({
              sender: "assistant",
              type: "search_results_link",
              text: `I found ${result.totalResults} part${
                result.totalResults !== 1 ? "s" : ""
              } matching your query.`,
              data: {
                results: result.results,
                query: result.query,
                totalResults: result.totalResults
              }
            })
          );
        } else if (
          result.aiResponse &&
          (!result.results || result.results.length === 0)
        ) {
          dispatch(
            addMessage({
              sender: "assistant",
              type: "text",
              text: result.aiResponse
            })
          );
        } else if (
          result.results &&
          result.results.length > 0 &&
          !result.aiResponse
        ) {
          dispatch(
            addMessage({
              sender: "assistant",
              type: "search_results_link",
              text: `I found ${result.totalResults} part${
                result.totalResults !== 1 ? "s" : ""
              } matching your query.`,
              data: {
                results: result.results,
                query: result.query,
                totalResults: result.totalResults
              }
            })
          );
        } else {
          dispatch(
            addMessage({
              sender: "assistant",
              type: "inquiry_form_prompt",
              text: "I couldn't find specific parts matching your query. Would you like to submit a part inquiry? Our sellers might have what you're looking for."
            })
          );
        }
      } else {
        dispatch(
          addMessage({
            sender: "assistant",
            type: "text",
            text: "Invalid query. Please try again with a different search term or be more specific."
          })
        );
      }
    } catch (error) {
      console.error("Error during AI search:", error);
      dispatch(
        addMessage({
          sender: "assistant",
          type: "text",
          text: "Sorry, I encountered an error processing your request. Please try again."
        })
      );
    }
  }, [inputValue, dispatch]);

  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === "Enter") {
        handleAskAssistant();
      }
    },
    [handleAskAssistant]
  );

  const scrollToHeroSection = () => {
    const heroSection = document.getElementById("hero-section");
    if (heroSection) {
      heroSection.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };

  const sectionAnimation = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: "easeOut" }
    }
  };

  const imageAnimation = {
    hidden: { opacity: 0, scale: 0.8, x: -50 },
    visible: {
      opacity: 1,
      scale: 1,
      x: 0,
      transition: { duration: 0.7, delay: 0.2, ease: "easeOut" }
    }
  };

  const contentAnimation = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, delay: 0.4, ease: "easeOut" }
    }
  };

  return (
    <motion.section
      id="ai-assistant-main"
      className="py-20 md:py-28 bg-gradient-to-br from-background via-secondary/10 to-secondary/30 relative overflow-hidden"
      variants={variants || sectionAnimation}
      initial="hidden"
      animate="visible"
      viewport={{ once: true, amount: 0.3 }}
    >
      <div className="absolute -top-20 -right-20 w-64 h-64 bg-primary/20 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-accent/20 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-6 relative">
        <div className="flex flex-col md:flex-row items-center justify-between gap-12 lg:gap-20">
          <motion.div
            className="w-full md:w-1/2 lg:w-2/5"
            variants={imageAnimation}
          >
            <div className="relative group">
              <img
                src="/assets/AI-Assistant-New-Hero.jpg"
                alt="PartsLokator AI Assistant Kato illustration"
                className="rounded-2xl shadow-2xl object-cover w-full h-auto max-h-[500px] transition-transform duration-700 group-hover:scale-[1.04]"
                style={{ aspectRatio: "3/4" }}
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </div>
          </motion.div>

          <motion.div
            className="w-full md:w-1/2 lg:w-3/5 text-center md:text-left"
            variants={contentAnimation}
          >
            <div className="flex items-center justify-center md:justify-start gap-1 sm:gap-3 mb-6">
              <Bot className="h-12 w-12 text-primary" />
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold leading-tight bg-gradient-to-r from-primary via-purple-500 to-accent text-transparent bg-clip-text">
                Kato — Your AI Parts Assistant
              </h1>
            </div>
            <p className="text-base sm:text-sm text-muted-foreground mb-10 max-w-xl mx-auto md:mx-0 leading-relaxed">
              Quickly find the exact parts you need — from bumpers to
              transmissions. Enter a part name, description, or query, and let
              Kato do the work.
            </p>
            {/* Search bar */}
            <div className="relative max-w-lg mx-auto md:mx-0">
              <Input
                type="text"
                placeholder="e.g., '2018 Honda Civic alternator'"
                value={inputValue}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                className="h-14 pl-6 pr-32 rounded-full text-base shadow-xl focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all"
              />
              <Button
                onClick={handleAskAssistant}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-11 rounded-full px-6 bg-gradient-to-r from-primary to-accent hover:opacity-90 transition"
                aria-label="Ask Kato"
              >
                <Send className="h-5 w-5 mr-2" />
                Ask
              </Button>
            </div>
            <div className="mt-10 flex flex-col justify-center md:justify-start items-start gap-4">
              <div className="flex gap-3 flex-wrap justify-center md:justify-start">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full px-4 shadow-sm border-primary/20 text-primary hover:bg-primary/10 transition"
                  disabled
                >
                  <UploadCloud className="h-4 w-4 mr-2" /> Upload Photo
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full px-4 shadow-sm border-primary/20 text-primary hover:bg-primary/10 transition"
                  disabled
                >
                  <Mic className="h-4 w-4 mr-2" /> Use Voice
                </Button>
              </div>

              <p className="text-sm text-muted-foreground italic mt-2 sm:mt-0 sm:ml-3">
                Coming soon — photo & voice search.
              </p>
            </div>
            <motion.div
              className="flex justify-center py-10 md:justify-start"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Button
                size="lg"
                onClick={scrollToHeroSection}
                variant="outline"
                className="group bg-gradient-to-r from-primary to-accent text-white hover:bg-accent px-6 py-3 whitespace-nowrap flex items-center justify-center gap-2"
              >
                <span>Search by Parts</span>
                <ArrowDown className="h-5 w-5 group-hover:translate-y-1 transition-transform duration-300" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default AiAssistantSection;
